import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Check, ArrowLeft, Zap, TrendingUp, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      if (!isAuthenticated) {
        navigate('/register', { state: { from: '/pricing' } });
        return;
      }
    }

    if (!isAuthenticated) {
      navigate('/register', { state: { from: '/pricing' } });
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/checkout/session`,
        { plan_id: planId, origin_url: originUrl },
        { headers: getAuthHeader() }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const planIcons = {
    free: Zap,
    starter: TrendingUp,
    professional: Crown
  };

  const planColors = {
    free: 'bg-stone-100 text-stone-700',
    starter: 'bg-[#94D2BD]/20 text-[#0A9396]',
    professional: 'bg-[#EE9B00]/20 text-[#EE9B00]'
  };

  if (!plans) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#005F73] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-stone-600">Chargement des plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-sand py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 text-[#005F73] hover:text-[#005F73]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-semibold text-[#005F73] mb-4 tracking-tight">
              Choisissez votre plan
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
              Découverte à 2 € · Sans engagement · Annulez à tout moment
            </p>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
          {Object.entries(plans).map(([planId, plan]) => {
            const Icon = planIcons[planId];
            const isCurrentPlan = user?.plan === planId;
            const isPopular = planId === 'professional';

            return (
              <div
                key={planId}
                data-testid={`plan-card-${planId}`}
                className={`bg-white rounded-2xl border-2 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)] transition-all duration-300 ${
                  isPopular ? 'border-[#EE9B00] relative' : 'border-stone-100 hover:border-[#005F73]/30'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#EE9B00] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Populaire
                    </span>
                  </div>
                )}

                <div className={`w-14 h-14 rounded-xl ${planColors[planId]} flex items-center justify-center mb-6`}>
                  <Icon className="w-7 h-7" strokeWidth={1.5} />
                </div>

                <h3 className="text-2xl font-semibold text-[#005F73] mb-2">{plan.name}</h3>
                
                <div className="mb-6">
                  {planId === 'free' ? (
                    <>
                      <div className="text-4xl font-semibold text-[#005F73]">
                        {plan.price}€
                      </div>
                      <p className="text-xs text-stone-400 mt-1">Paiement unique — 1 simulation incluse. Non renouvelable.</p>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-semibold text-[#005F73]">
                        {plan.price}€
                        <span className="text-lg font-normal text-stone-600">/mois</span>
                      </div>
                      <p className="text-xs text-stone-400 mt-1">Renouvellement automatique chaque mois. Annulable à tout moment.</p>
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-stone-600">
                      <Check className="w-5 h-5 text-[#0A9396] flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button
                    disabled
                    className="w-full rounded-xl h-12 bg-stone-200 text-stone-600 cursor-not-allowed"
                  >
                    Plan actuel
                  </Button>
                ) : (
                  <Button
                    data-testid={`btn-subscribe-${planId}`}
                    onClick={() => handleSubscribe(planId)}
                    disabled={loading && selectedPlan === planId}
                    className={`w-full rounded-xl h-12 font-medium ${
                      isPopular
                        ? 'bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white'
                        : 'bg-[#005F73] hover:bg-[#005F73]/90 text-white'
                    }`}
                  >
                    {loading && selectedPlan === planId ? 'Chargement...' : 'Souscrire'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-semibold text-[#005F73] mb-8 text-center">Questions fréquentes</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-[#005F73] mb-2">Comment commencer ?</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Inscrivez-vous et activez l'offre Découverte à 2 € pour réaliser votre première simulation. Pour créer plus de projets, choisissez un abonnement Starter ou Professional.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-[#005F73] mb-2">Puis-je changer de plan à tout moment ?</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements prennent effet immédiatement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-[#005F73] mb-2">Les paiements sont-ils sécurisés ?</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Oui, tous les paiements sont traités de manière sécurisée via Stripe. Nous ne stockons aucune information de carte bancaire.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-[#005F73] mb-2">Comment annuler mon abonnement ?</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Vous pouvez annuler votre abonnement à tout moment depuis votre tableau de bord. Aucun frais d'annulation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
